import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { AuditTable } from '../../components/audit-table';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { api } from '../../lib/api';
import { auditBaseKey, auditQuery } from '../../lib/queries';

export const Route = createFileRoute('/_auth/audit')({
  beforeLoad: ({ context }) => {
    if (context.session.role !== 'farmer') throw redirect({ to: '/' });
  },
  component: AuditPage,
});

function AuditPage() {
  const queryClient = useQueryClient();
  const [actorFilter, setActorFilter] = useState('');
  const [sheetFilter, setSheetFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [undoTarget, setUndoTarget] = useState<string | null>(null);

  const params = {
    ...(actorFilter && { actor: actorFilter }),
    ...(sheetFilter && { target_sheet: sheetFilter }),
    limit: 50,
    offset,
  };

  const { data, isLoading } = useQuery(auditQuery(params));

  const undoMutation = useMutation({
    mutationFn: (auditId: string) => api.post('/api/audit/undo', { audit_id: auditId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditBaseKey });
      setUndoTarget(null);
    },
  });

  return (
    <div className="space-y-4 max-w-7xl">
      <h1 className="text-2xl font-bold">Audit Trail</h1>

      {/* Filter bar */}
      <div className="flex gap-3 items-center flex-wrap">
        <Select value={actorFilter} onValueChange={setActorFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All actors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All actors</SelectItem>
            <SelectItem value="claude">Claude</SelectItem>
            <SelectItem value="dashboard">Dashboard</SelectItem>
            <SelectItem value="migration">Migration</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sheetFilter} onValueChange={setSheetFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All sheets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All sheets</SelectItem>
            <SelectItem value="herd_register">herd_register</SelectItem>
            <SelectItem value="feeding_log">feeding_log</SelectItem>
            <SelectItem value="feed_inventory">feed_inventory</SelectItem>
            <SelectItem value="rations">rations</SelectItem>
            <SelectItem value="events">events</SelectItem>
          </SelectContent>
        </Select>

        {(actorFilter || sheetFilter) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setActorFilter('');
              setSheetFilter('');
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-ndd-400 py-8 text-center">Loading…</div>
      ) : (
        <>
          <AuditTable entries={data?.entries ?? []} onUndo={setUndoTarget} />

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-ndd-400">{data?.total ?? 0} total entries</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - 50))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!data || offset + 50 >= data.total}
                onClick={() => setOffset((o) => o + 50)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Undo confirmation dialog */}
      <Dialog
        open={!!undoTarget}
        onOpenChange={(open) => {
          if (!open) setUndoTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Undo</DialogTitle>
            <DialogDescription>
              This will reverse the operation and create a new audit entry. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => undoTarget && undoMutation.mutate(undoTarget)}
              disabled={undoMutation.isPending}
            >
              Undo Operation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
